import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { WatchDemoModal } from "@/components/WatchDemoModal";
import { exploreSampleAndGo } from "@/lib/sampleData";
import {
  UploadSimple, UsersThree, ChartLineUp, CheckCircle, Circle, ArrowRight, X, Sparkle, PlayCircle, FolderOpen,
} from "@phosphor-icons/react";

const TOUR = [
  { icon: UploadSimple, title: "Import in minutes", desc: "Upload your existing Excel or CSV — RecoverOS auto-maps the columns and builds your portfolio instantly. No ERP, no setup." },
  { icon: ChartLineUp, title: "See what's stuck", desc: "The Money Stuck Analyzer™ and Recovery Health Score show exactly how much is recoverable and what to chase first." },
  { icon: UsersThree, title: "Work as a team", desc: "Invite admins, managers and agents with role-based access, then track activity and recoveries from one dashboard." },
];

export function OnboardingWelcome({ stats }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = ["super_admin", "client_admin"].includes(user.role);
  const flag = `recoveros_onboarded_${user.user_id || user.email}`;
  const insightFlag = `recoveros_insight_seen_${user.user_id || user.email}`;

  const [showWelcome, setShowWelcome] = useState(false);
  const [slide, setSlide] = useState(0);
  const [userCount, setUserCount] = useState(null);
  const [dismissed, setDismissed] = useState(true);
  const [watchOpen, setWatchOpen] = useState(false);
  const canSample = user.role === "client_admin";

  useEffect(() => {
    if (!isAdmin) return;
    if (!localStorage.getItem(flag)) setShowWelcome(true);
    setDismissed(localStorage.getItem(`${flag}_checklist_hidden`) === "1");
    api.get("/users").then((r) => setUserCount(Array.isArray(r.data) ? r.data.length : (r.data?.users?.length ?? null))).catch(() => setUserCount(null));
  }, [isAdmin, flag]);

  if (!isAdmin) return null;

  const finishWelcome = () => { localStorage.setItem(flag, "1"); setShowWelcome(false); setDismissed(false); };

  const accountsDone = (stats?.total_accounts || 0) > 0;
  const teamDone = (userCount ?? 1) > 1;
  const insightDone = localStorage.getItem(insightFlag) === "1";
  const steps = [
    { key: "import", label: "Import your accounts", desc: "Upload Excel/CSV to build your portfolio", done: accountsDone, cta: "Import", to: "/import" },
    { key: "team", label: "Invite your team", desc: "Add managers & agents with role-based access", done: teamDone, cta: "Invite", to: "/users" },
    { key: "insight", label: "View your Money Stuck insights", desc: "See what's recoverable and your Health Score", done: insightDone, cta: "View", to: "/money-stuck", onGo: () => localStorage.setItem(insightFlag, "1") },
  ];
  const completed = steps.filter((s) => s.done).length;
  const allDone = completed === steps.length;

  return (
    <>
      <Dialog open={showWelcome} onOpenChange={(o) => { if (!o) finishWelcome(); }}>
        <DialogContent className="sm:max-w-lg rounded-sm" data-testid="onboarding-welcome">
          <VisuallyHidden>
            <DialogTitle>Welcome to RecoverOS</DialogTitle>
            <DialogDescription>A quick tour to get you started with RecoverOS.</DialogDescription>
          </VisuallyHidden>
          <div className="text-center">
            <div className="h-12 w-12 rounded-full bg-[#0B3D91]/10 flex items-center justify-center mx-auto">
              <Sparkle size={26} weight="fill" className="text-[#0B3D91]" />
            </div>
            <div className="mt-3 text-xs font-bold uppercase tracking-widest text-[#0B3D91]">Welcome to RecoverOS</div>
            {(() => { const T = TOUR[slide]; const Icon = T.icon; return (
              <div className="mt-5" data-testid={`welcome-slide-${slide}`}>
                <div className="h-14 w-14 rounded-sm bg-[#16A34A]/10 flex items-center justify-center mx-auto"><Icon size={28} weight="bold" className="text-[#16A34A]" /></div>
                <h2 className="mt-4 font-heading font-bold text-xl">{T.title}</h2>
                <p className="mt-2 text-sm text-muted-foreground max-w-sm mx-auto">{T.desc}</p>
              </div>
            ); })()}
            <div className="flex items-center justify-center gap-1.5 mt-5">
              {TOUR.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === slide ? "w-5 bg-[#0B3D91]" : "w-1.5 bg-border"}`} />)}
            </div>
            {slide < TOUR.length - 1 ? (
              <div className="mt-6 flex items-center justify-between gap-3">
                <button onClick={finishWelcome} className="text-sm text-muted-foreground hover:text-foreground" data-testid="welcome-skip">Skip</button>
                <Button className="rounded-sm gap-1.5 bg-[#0B3D91] hover:bg-[#0a3578]" onClick={() => setSlide((s) => s + 1)} data-testid="welcome-next">Next <ArrowRight size={15} weight="bold" /></Button>
              </div>
            ) : (
              <div className="mt-6 space-y-2">
                <Button className="w-full rounded-sm gap-1.5 bg-[#16A34A] hover:bg-[#138a3e]" onClick={() => { finishWelcome(); navigate("/import"); }} data-testid="welcome-get-started">
                  <UploadSimple size={15} weight="bold" /> Import my portfolio
                </Button>
                {canSample && (
                  <Button variant="outline" className="w-full rounded-sm gap-1.5" onClick={() => { finishWelcome(); exploreSampleAndGo(toast); }} data-testid="welcome-sample">
                    <FolderOpen size={15} weight="bold" /> Explore sample portfolio
                  </Button>
                )}
                <Button variant="outline" className="w-full rounded-sm gap-1.5" onClick={() => setWatchOpen(true)} data-testid="welcome-watch-demo">
                  <PlayCircle size={15} weight="bold" /> Watch the 4-min demo
                </Button>
                <button onClick={finishWelcome} className="w-full text-sm text-muted-foreground hover:text-foreground pt-1" data-testid="welcome-skip">Skip for now</button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {!dismissed && !allDone && (
        <div className="border border-[#0B3D91]/20 bg-[#0B3D91]/[0.03] p-5 mb-6" data-testid="getting-started-checklist">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <h3 className="font-heading font-bold text-lg text-[#0B3D91]">Getting started</h3>
              <p className="text-xs text-muted-foreground mt-0.5">{completed} of {steps.length} complete — finish setup to get the most out of RecoverOS.</p>
              <div className="mt-2 h-1.5 w-48 bg-border rounded-full overflow-hidden"><div className="h-full bg-[#16A34A] transition-all" style={{ width: `${(completed / steps.length) * 100}%` }} /></div>
            </div>
            <button onClick={() => { localStorage.setItem(`${flag}_checklist_hidden`, "1"); setDismissed(true); }} className="text-muted-foreground hover:text-foreground" data-testid="checklist-dismiss" aria-label="Dismiss"><X size={16} weight="bold" /></button>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {steps.map((s) => (
              <div key={s.key} className="border border-border bg-card p-3 flex flex-col" data-testid={`checklist-step-${s.key}`}>
                <div className="flex items-center gap-2">
                  {s.done ? <CheckCircle size={18} weight="fill" className="text-[#16A34A]" /> : <Circle size={18} className="text-muted-foreground/50" />}
                  <span className={`text-sm font-semibold ${s.done ? "text-muted-foreground line-through" : ""}`}>{s.label}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1 flex-1">{s.desc}</p>
                {!s.done && (
                  <button onClick={() => { s.onGo?.(); navigate(s.to); }} className="mt-2 self-start inline-flex items-center gap-1 text-xs font-semibold text-[#0B3D91] hover:underline" data-testid={`checklist-cta-${s.key}`}>
                    {s.cta} <ArrowRight size={12} weight="bold" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <WatchDemoModal open={watchOpen} onOpenChange={setWatchOpen} source="first-login-welcome"
        onSample={canSample ? () => exploreSampleAndGo(toast) : undefined}
        onImport={() => navigate("/import")} />
    </>
  );
}
